import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import { useToast } from './ui/Toast';
import CodeEditor from './ui/CodeEditor';
import ResizableSplitter from './ui/ResizableSplitter';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { useWebSocket } from '../utils/useWebSocket';
import { getLiveDraft, setLiveDraft } from '../utils/liveConnections';
import { isTauri } from '../utils/isTauri';
import HeaderKvEditor from './ui/HeaderKvEditor';
import { prepareWsConnect, prepareWsMessage } from '../utils/protocolVariables';
import WebSocketSettingsPanel from './WebSocketSettingsPanel';
import StreamRequestBar from './ui/StreamRequestBar';
import StreamLogTable from './ui/StreamLogTable';
import Tooltip from './ui/Tooltip';
import ShortcutHint from './ui/ShortcutHint';
import { recordFeature, REQUEST } from '../services/telemetry';


/** Return a human-readable status label. */
function statusLabel(status) {
  switch (status) {
    case 'connecting':    return 'Connecting…';
    case 'connected':     return 'Connected';
    case 'disconnected':  return 'Disconnected';
    case 'error':         return 'Error';
    default:              return 'Idle';
  }
}

/**
 * WebSocket UI: connection bar + message composer + message log.
 *
 * {{variables}} in the url, header keys/values and outgoing messages are resolved with
 * the HTTP resolver over `variableScopes` (utils/protocolVariables).
 *
 * @param {{ request: object, onDataChange?: (patch: object) => void, variableScopes?: object }} props
 */
const WebSocketBuilder = ({ request, onDataChange, variableScopes }) => {
  const { showToast } = useToast();

  // ── URL state — kept in sync with the saved request object ──────────────
  const [wsUrl, setWsUrl] = useState(request?.url ?? '');

  // Keep URL in sync if the parent pushes a new request object (e.g. tab switch)
  const prevRequestIdRef = useRef(request?.id);
  useEffect(() => {
    if (request?.id && request.id !== prevRequestIdRef.current) {
      prevRequestIdRef.current = request.id;
      setWsUrl(request.url ?? '');
      setWsHeaders(request.wsHeaders ?? []);
      setWsSettings(request.wsSettings ?? {});
      setComposerTab('message');
    }
  }, [request?.id, request?.url, request?.wsHeaders, request?.wsSettings]);

  // ── Composer state ───────────────────────────────────────────────────────
  // the unsent message belongs to the TAB, like the connection: a tab switch remounts this builder
  const [composerText, setComposerTextState] = useState(() => getLiveDraft(request?.id));
  const setComposerText = useCallback((text) => { setComposerTextState(text); setLiveDraft(request?.id, text); }, [request?.id]);
  useEffect(() => { setComposerTextState(getLiveDraft(request?.id)); }, [request?.id]); // one builder can serve several tabs
  const [composerLang, setComposerLang] = useState('json');
  // Message · Headers · Settings — headers and settings are the REQUEST's (saved with it), applied at the next Connect
  const [composerTab, setComposerTab] = useState('message');
  const [wsHeaders, setWsHeaders] = useState(request?.wsHeaders ?? []);
  const [wsSettings, setWsSettings] = useState(request?.wsSettings ?? {});
  const native = isTauri(); // the desktop client: custom headers, handshake details, TLS / size / timeout options
  const [splitLayoutTick, setSplitLayoutTick] = useState(0);

  const composerOptions = [
    { value: 'json', label: 'JSON' },
    { value: 'text', label: 'Text' },
  ];

  // ── WebSocket hook ───────────────────────────────────────────────────────
  // Keyed by the TAB: the connection and its log outlive this component (tab switches).
  const { status, messages, dropped, connect, disconnect, sendMessage, clearMessages } = useWebSocket({
    tabId: request?.id,
    onStatusChange: (next) => {
      if (next === 'connected') {
        showToast('WebSocket connected', 'success');
      } else if (next === 'disconnected') {
        showToast('WebSocket disconnected', 'info');
      } else if (next === 'error') {
        showToast('WebSocket connection error', 'error');
      }
    },
  });


  // ── Handlers ────────────────────────────────────────────────────────────
  const handleConnect = useCallback(() => {
    recordFeature(REQUEST.WEBSOCKET);   // identifier-free: this kind was used today
    const trimmed = wsUrl.trim();
    if (!trimmed) {
      showToast('Enter a WebSocket URL (ws:// or wss://)', 'warning');
      return;
    }
    // checked on the SUBSTITUTED url: `{{wsBase}}/feed` connects when wsBase is ws://…
    const plan = prepareWsConnect({ url: trimmed, headers: wsHeaders }, variableScopes);
    if (!/^wss?:\/\//i.test(plan.url)) {
      const missing = plan.unresolved.length ? ` (${plan.unresolved.map((n) => `{{${n}}}`).join(', ')} is not defined)` : '';
      showToast(`URL must start with ws:// or wss://${missing}`, 'warning');
      return;
    }
    // Persist URL change before connecting — as typed, never the substituted value.
    onDataChange?.({ url: trimmed });
    connect(plan.url, { headers: plan.headers, settings: wsSettings, displayUrl: plan.displayUrl, shownHeaders: plan.shownHeaders });
  }, [wsUrl, wsHeaders, wsSettings, variableScopes, connect, onDataChange, showToast]);

  const handleDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  const handleSend = useCallback(() => {
    const text = composerText.trim();
    if (!text) return;
    if (status !== 'connected') {
      showToast('Not connected', 'warning');
      return;
    }
    const out = prepareWsMessage(text, variableScopes);
    sendMessage(out.text, out.shown);
  }, [composerText, status, variableScopes, sendMessage, showToast]);

  const handleUrlKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (status === 'connected' || status === 'connecting') {
          handleDisconnect();
        } else {
          handleConnect();
        }
      }
    },
    [status, handleConnect, handleDisconnect]
  );

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';
  const isActive = isConnected || isConnecting;

  return (
    <div className="ws-builder h-full flex flex-col bg-white">
      {/* ── Request bar — the HTTP builder's: URL in the field group, Connect where Send is ── */}
      <StreamRequestBar
        inputProps={{
          className: 'ws-builder__url-input',
          placeholder: 'ws://localhost:8080 or wss://api.example.com/ws',
          value: wsUrl,
          // saved as typed, like headers: a tab switch re-reads the request
          onChange: (e) => { setWsUrl(e.target.value); onDataChange?.({ url: e.target.value }); },
          onKeyDown: handleUrlKeyDown,
          disabled: isActive,
          'aria-label': 'WebSocket URL',
        }}
        state={{ status, label: statusLabel(status) }}
        action={isActive
          ? { label: isConnecting ? 'Cancel' : 'Disconnect', onClick: handleDisconnect, danger: true, className: 'ws-builder__btn--disconnect' }
          : { label: 'Connect', onClick: handleConnect, className: 'ws-builder__btn--connect' }}
      />

      {/* ── Composer + Log (resizable) ────────────────────────────────── */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <ResizableSplitter
          direction="vertical"
          initialSplit={35}
          minSize={0}
          maxSize={100}
          minFirstPanelPx={80}
          minSecondPanelPx={80}
          className="h-full"
          onResizeEnd={() => setSplitLayoutTick((n) => n + 1)}
        >
          {/* ── Top: Message composer ──────────────────────────────── */}
          <div className="ws-builder__composer flex flex-col h-full" style={{ minHeight: 0 }}>
            {/* only the tab in this row — it is then the HTTP builder's 17 px tab row */}
            <div className="sb-tabs flex-shrink-0" role="tablist">
              {[['message', 'Message'], ['headers', 'Headers'], ['settings', 'Settings']].map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={composerTab === id} className={`sb-tab${composerTab === id ? ' sb-tab--active' : ''}`} onClick={() => setComposerTab(id)}>
                  {label}
                </button>
              ))}
            </div>
            {composerTab === 'headers' && (
              <div className="flex-1 overflow-auto panel-scroll" style={{ minHeight: 0 }}>
                <div className="editor-tab-body">
                  <HeaderKvEditor rows={wsHeaders} idPrefix="wsh" disabled={isActive || !native} onChange={(rows) => { setWsHeaders(rows); onDataChange?.({ wsHeaders: rows }); }} />
                  {!native && <p className="ws-settings__note">Handshake headers need the desktop app — a browser WebSocket cannot send them.</p>}
                </div>
              </div>
            )}
            {composerTab === 'settings' && (
              <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
                <WebSocketSettingsPanel settings={wsSettings} native={native} disabled={isActive} onChange={(next) => { setWsSettings(next); onDataChange?.({ wsSettings: next }); }} />
              </div>
            )}
            <div className="flex-1 overflow-hidden sb-editor" style={{ minHeight: 0, display: composerTab === 'message' ? undefined : 'none' }}>
              <CodeEditor
                hideChrome
                value={composerText}
                onChange={setComposerText}
                language={composerLang}
                placeholder={composerLang === 'json' ? '{\n  \n}' : 'Enter message…'}
                height="100%"
                splitLayoutTick={splitLayoutTick}
                onCtrlEnter={handleSend}
              />
            </div>
            {/* under the editor: how the message is written, and Send (Ctrl/⌘ + Enter) */}
            <div className="sb-composer-foot flex-shrink-0" style={composerTab === 'message' ? undefined : { display: 'none' }}>
              <CompactSelectPicker
                value={composerLang}
                onChange={setComposerLang}
                options={composerOptions}
                aria-label="Message language"
                className="sb-composer-foot__picker"
              />
              <Tooltip content={<ShortcutHint label="Send message" keys={['mod', 'Enter']} />} side="top">
                <button
                  type="button"
                  className="ws-builder__btn ws-builder__btn--send"
                  onClick={handleSend}
                  disabled={!isConnected || !composerText.trim()}
                >
                  <Send size={13} aria-hidden />
                  <span>Send</span>
                </button>
              </Tooltip>
            </div>
          </div>

          {/* ── Bottom: the log, as a table (newest first; connection events are rows too) ── */}
          <div className="ws-builder__log flex flex-col h-full" style={{ minHeight: 0 }}>
            <StreamLogTable
              key={request?.id}
              entries={messages}
              dropped={dropped}
              onClear={clearMessages}
              emptyText={isActive ? 'Waiting for messages…' : 'Connect to start receiving messages.'}
              handshakeNote="Handshake details need the desktop app — a browser connection does not show them."
            />
          </div>
        </ResizableSplitter>
      </div>
    </div>
  );
};

export default WebSocketBuilder;
